import { Component, Inject, OnInit } from '@angular/core';
import { PublishService } from '@service/publish.service';
import { AuthService } from '@service/auth.service';
import { ApprovalRequest } from 'icreate/common/models/approval-request';
import { Vessel } from 'icreate/common/models/vessel';
import { UserCompanyService } from '@service/user-company.service';
import {
    approvalReasonCommentLength,
    approvalReasonOtherLength,
    approvalReasonValidator,
} from 'icreate/common/validators/approval-request/approval-request-validator';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { VersionsHistoryService } from '@service/versions-history.service';
import { PublishedVersion } from 'icreate/common/models/versions-history';

@Component({
    selector: 'app-request-approval-dialog',
    templateUrl: './request-approval-dialog.component.html',
    styleUrls: ['./request-approval-dialog.component.css'],
    standalone: false,
})
export class RequestApprovalDialogComponent implements OnInit {
    readonly OTHER_REASON_MAX_LENGTH = approvalReasonOtherLength;
    readonly COMMENT_MAX_LENGTH = approvalReasonCommentLength;

    approversList: { name: string; email: string; oid: string }[];
    selectedApprover: { name: string; email: string; oid: string } = {
        name: null,
        email: null,
        oid: null,
    };

    versions: PublishedVersion[];
    currentProjectVersion: string;

    customerRequestCheckbox: boolean = false;
    misconfigurationCheckbox: boolean = false;
    internalRequestCheckbox: boolean = false;
    otherCheckbox: boolean = false;
    otherReason: string;
    comment: string;

    isLoading: boolean = false;

    constructor(
        private publishService: PublishService,
        private authService: AuthService,
        private userCompanyService: UserCompanyService,

        @Inject(MAT_DIALOG_DATA)
        public data: {
            vessel: Vessel;
            shipyardiApi: string;
        },
        private dialogRef: MatDialogRef<RequestApprovalDialogComponent>,
        private versionHistoryService: VersionsHistoryService
    ) {}

    async ngOnInit() {
        this.isLoading = true;
        this.currentProjectVersion = this.data.vessel.projectVersion;
        const shipyardId = this.data.shipyardiApi;
        const vesselId = this.data.vessel.iAPIVesselId;
        const currentUser = this.authService.getCurrentUser();
        this.versions = await this.versionHistoryService.getVersionsList(shipyardId, vesselId);
        this.approversList = (await this.userCompanyService.getUsersCompany(currentUser.oid)).filter(
            approver => approver.oid !== this.data.vessel.ownerOid || !approver.name || !approver.oid || !approver.email
        );
        this.isLoading = false;
    }

    isButtonDisabled(): boolean {
        return (
            !this.selectedApprover?.name ||
            !this.selectedApprover?.email ||
            !this.selectedApprover?.oid ||
            !this.data.vessel?.projectVersion ||
            !this.hasApprovalReason() ||
            this.otherReason?.length > 200
        );
    }

    hasApprovalReason(): boolean {
        if (this.otherCheckbox && (!this.otherReason || this.otherReason.length == 0)) return false;
        return (
            this.customerRequestCheckbox ||
            this.misconfigurationCheckbox ||
            this.internalRequestCheckbox ||
            (this.otherCheckbox && !!this.otherReason)
        );
    }

    async requestApproval() {
        if (this.isButtonDisabled() || !this.data.shipyardiApi || !this.data.vessel) {
            return;
        }
        try {
            this.isLoading = true;
            const currentUser = this.authService.getCurrentUser();
            const approvalRequest: ApprovalRequest = {
                shipyardiApi: this.data.shipyardiApi,
                iAPIVesselId: this.data.vessel.iAPIVesselId,
                shipyardId: this.data.vessel.shipyardId,
                vesselId: this.data.vessel.id,
                vesselName: this.data.vessel.name,
                approverName: this.selectedApprover.name,
                approverEmail: this.selectedApprover.email,
                approverOid: this.selectedApprover.oid,
                ownerName: this.data.vessel.ownerName,
                ownerEmail: currentUser.email,
                ownerOid: this.data.vessel.ownerOid,
                version: this.data.vessel.projectVersion,
                reason: {
                    customerRequest: this.customerRequestCheckbox,
                    misconfiguration: this.misconfigurationCheckbox,
                    internalRequest: this.internalRequestCheckbox,
                    other: this.otherCheckbox ? this.otherReason : undefined,
                    comment: this.comment,
                },
            };
            await this.publishService.requestPublish(approvalRequest);
            this.isLoading = false;
            this.dialogRef.close(true);
        } catch (err) {
            console.error(err);
            this.clearForm();
        }
    }

    clearForm() {
        this.data.vessel.projectVersion = this.currentProjectVersion;
        this.dialogRef.close();
    }

    public get validatorFunction() {
        return approvalReasonValidator;
    }
}
